import { useState, useEffect } from "react";
import { api } from "../api";
import { Badge, Btn, Field, formatWhen, Input } from "../components/Shared";
import { fmtBytes } from "../wizard/SubmissionWizard";

/**
 * How full this group's bucket is.
 *
 * Nothing showed this until 2026-09-07, and the cost was concrete: the bucket
 * behind cf-binf-h15008 filled up while the Storage card beside this one showed
 * a green "Configured" badge and `s3_health_ok` was true — the health check asks
 * whether the bucket answers, not whether it has room. A 246 GB upload found out
 * at part 7502 of 9500 and was retried three times over three days.
 *
 * **Capacity is typed in, and the card says so.** It is not an S3 concept, and
 * Ceph RGW keeps its quota behind an admin API this portal holds no credential
 * for. Presenting a number somebody entered as though it were measured would be
 * worse than showing nothing.
 *
 * Unknowns stay unknown: no measurement renders as "not measured yet", never as
 * 0 bytes used, because those are opposite facts.
 */
export default function StorageUsageCard({ groupId, canEdit }) {
  const [usage, setUsage] = useState(null);
  const [draft, setDraft] = useState("");
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!groupId) return;
    setError(null);
    api.get(`/api/groups/${groupId}/storage-usage`)
      .then(u => {
        setUsage(u);
        setDraft(u.capacity_bytes == null ? "" : String(Math.round(u.capacity_bytes / 1024 ** 3)));
      })
      .catch(e => setError(e.message));
  }, [groupId]);

  const saveCapacity = async () => {
    setBusy(true); setError(null);
    try {
      const gib = draft.trim() === "" ? null : Number(draft);
      const u = await api.put(`/api/groups/${groupId}/storage-capacity`,
                              { capacity_bytes: gib == null ? null : Math.round(gib * 1024 ** 3) });
      setUsage(u);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  if (!usage) return null;

  // A stale figure is still a figure. The backend hands back the last
  // SUCCESSFUL reading alongside the latest error, so a bucket that has
  // stopped answering shows what it last held, clearly marked — rather than a
  // blank, or the failed attempt's zero.
  const measured = usage.used_bytes != null;
  const pct = usage.used_fraction == null ? null : Math.min(100, usage.used_fraction * 100);

  return (
    <div style={{ marginTop: 20, paddingTop: 20, borderTop: "1px solid #e5e7eb" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
        <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700 }}>Space</h4>
        {usage.nearly_full && <Badge color="red">Nearly full</Badge>}
      </div>

      {error && <div role="alert" style={{ margin: "8px 0", color: "#dc2626", fontSize: 13 }}>{error}</div>}

      {usage.measure_error ? (
        <p style={{ margin: "0 0 12px", fontSize: 13, color: "#b45309" }}>
          The bucket could not be measured: {usage.measure_error}. The figure below, if any, is
          from an earlier reading and may be out of date.
        </p>
      ) : null}

      {/* Which bucket, and who else is on it. The card used to imply "how much
          does this group use" while the measurement answered "how full is this
          bucket" — a listing has no Prefix, so it was always the second. Naming
          the bucket is what makes the figure honest, and naming the other
          groups is what stops "somebody else filled it" reading as a bug in the
          number. In production all seven projects share one bucket. */}
      {usage.bucket && (
        <p style={{ margin: "0 0 8px", fontSize: 12, color: "#6b7280" }}>
          These figures are for the bucket <code>{usage.bucket}</code>
          {usage.shared_with?.length
            ? <>, which is <strong>shared with {usage.shared_with.join(", ")}</strong>. The
                capacity below is the whole bucket&apos;s and is held jointly.</>
            : "."}
        </p>
      )}

      <p style={{ margin: "0 0 12px", fontSize: 13, color: "#6b7280" }}>
        {measured
          ? <>Holding <strong>{fmtBytes(usage.used_bytes)}</strong> across{" "}
              {usage.object_count?.toLocaleString()} object(s).{" "}
              {/* Stated separately rather than folded into the total, because
                  it cannot be deleted the way an object can: an incomplete
                  multipart upload is invisible to a listing, can never be
                  completed, and is billed until it is aborted. */}
              {usage.incomplete_known && usage.incomplete_bytes > 0 && (
                <>Plus <strong>{fmtBytes(usage.incomplete_bytes)}</strong> in{" "}
                  {usage.incomplete_count?.toLocaleString()} incomplete upload(s), which a
                  listing cannot see and which count against the capacity.{" "}</>
              )}
              {/* And when the endpoint would not answer, say so. "We could not
                  look" must not read as "there are none". */}
              {measured && !usage.incomplete_known && (
                <>Incomplete uploads could not be read from this endpoint, so the figure is a
                  lower bound.{" "}</>
              )}
              {usage.capacity_bytes == null
                ? "No capacity has been recorded, so nothing here can say whether an upload will fit."
                : usage.over_capacity
                  // Never "118% full". A bucket cannot hold more than its own
                  // ceiling, so a reading above it says the ceiling is wrong —
                  // and the ceiling is the only figure here nobody measured.
                  ? <>That is <strong>more than the {fmtBytes(usage.capacity_bytes)} recorded</strong>{" "}
                      as its capacity, so the recorded figure is out of date. Uploads are still
                      being refused against it — correct it below.</>
                  : <><strong>{fmtBytes(usage.free_bytes)}</strong> of the recorded{" "}
                      {fmtBytes(usage.capacity_bytes)} is free.</>}
            </>
          : "Not measured yet. The sweep totals each bucket every few hours."}
      </p>

      {pct != null && (
        // Presentational only — the words above carry the same facts, because a
        // bar nobody can read a number off is not an answer to "will this fit".
        <div aria-hidden="true" style={{ height: 8, borderRadius: 4, background: "#e5e7eb",
                                         overflow: "hidden", marginBottom: 12, maxWidth: 420 }}>
          <div style={{ width: `${pct}%`, height: "100%",
                        background: usage.nearly_full ? "#dc2626" : "#059669" }} />
        </div>
      )}

      {canEdit && (
        <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div style={{ minWidth: 200 }}>
            <Field label="Recorded capacity (GB)"
                   help="Typed in, not measured — S3 does not report a bucket's size limit. Leave blank for no limit.">
              <Input type="number" min="1" value={draft}
                     onChange={e => setDraft(e.target.value)} />
            </Field>
          </div>
          <Btn onClick={saveCapacity} disabled={busy} style={{ marginBottom: 2 }}>
            {busy ? "Saving…" : "Save capacity"}
          </Btn>
        </div>
      )}

      {/* When, and by whom. The capacity is the one number on this card that was
          typed rather than measured, so how old it is IS the reason to trust it
          or not — a ceiling entered before IT raised the quota is what turns a
          bucket with room into a refused upload. */}
      {usage.capacity_bytes != null && (
        <p style={{ margin: "10px 0 0", fontSize: 12, color: "#6b7280" }}>
          {usage.capacity_updated_at
            ? <>Recorded by {usage.capacity_updated_by || "an unknown account"} on{" "}
                {formatWhen(usage.capacity_updated_at)}.</>
            : "There is no record of when this capacity was set."}
        </p>
      )}
    </div>
  );
}
